import {
  DevicePublishedContent,
  DeviceHeartbeat,
} from '@raydiant/api-client-js';
import * as RE from '@raydiant/playlist-rule-engine';
import {
  PlaylistWindowManager,
  PlaylistWindowManagerState,
  PresentationWindowRenderer,
} from '@raydiant/playlist-window-manager';
import { useEffect, useState } from 'react';

export default function useWindowManager(
  deviceId: string,
  heartbeat: DeviceHeartbeat | null,
  publishedContent: DevicePublishedContent | null,
  renderer: PresentationWindowRenderer | null,
  logger: Logger,
  appRendererUrl?: string,
  appRendererOfflineSupport?: boolean,
): [PlaylistWindowManager | null, boolean] {
  const [
    windowManager,
    setWindowManager,
  ] = useState<PlaylistWindowManager | null>(null);
  const [ruleEngine, setRuleEngine] = useState<RE.RuleEngine | null>(null);
  const [
    windowManagerState,
    setWindowManagerState,
  ] = useState<PlaylistWindowManagerState | null>(null);
  const [isEmpty, setIsEmpty] = useState(false);

  // Save off device name to pass as a dependency to the useEffect below.
  // Since we only care about the name, this ensures we don't recreate the
  // window manager after every heartbeat
  const deviceName = heartbeat?.name ?? null;

  // Create a rule engine and manager state whenever published content changes.
  useEffect(() => {
    if (!publishedContent) return;
    const {
      playlistId,
      playlists,
      presentations,
      applicationVersions,
      themes,
      applications,
    } = publishedContent;
    if (!playlistId) return;
    if (!playlists) return;
    if (!presentations) return;
    if (!applicationVersions) return;
    if (!themes) return;
    if (!applications) return;

    logger.info(`Creating rule engine for playlist '${playlistId}'`);

    setRuleEngine(
      RE.createRuleEngineFromAPI(
        playlistId,
        playlists,
        presentations,
        applicationVersions,
        applications,
      ),
    );

    setWindowManagerState(
      new PlaylistWindowManagerState({
        presentations,
        applicationVersions,
        themes,
        publishedAt: '',
      }),
    );
  }, [publishedContent, logger]);

  // Create or update the window manager whenever device or published content changes.
  useEffect(() => {
    if (!ruleEngine) return;
    if (!windowManagerState) return;
    if (deviceName === null) return;

    if (windowManager) {
      // Don't update the window manager if the rule engine and state are the same.
      if (
        ruleEngine === windowManager.getRuleEngine() &&
        windowManagerState === windowManager.getState()
      ) {
        return;
      }

      logger.info(
        'Reinitializing window manager with new state and rule engine',
      );

      windowManager
        .reinitialize(windowManagerState, ruleEngine, true)
        .then(() => {
          if (windowManager.isEmpty()) {
            logger.warn(`Updated playlist window manager with empty playlist`);
          }
          setIsEmpty(windowManager.isEmpty());
        });
    } else {
      logger.info(`Creating new playlist window manager`);
      const newWindowManager = new PlaylistWindowManager({
        ruleEngine,
        state: windowManagerState,
        appRendererUrl,
        appRendererOfflineSupport,
        logger,

        device: {
          id: deviceId,
          name: deviceName,
        },

        onEnableInterruptLock: () => {
          logger.info(`Enable interrupt lock`);
        },

        onDisableInterruptLock: () => {
          logger.info(`Disable interrupt lock`);
        },
      });

      setWindowManager(newWindowManager);
      setIsEmpty(newWindowManager.isEmpty());
    }
  }, [
    ruleEngine,
    windowManagerState,
    deviceId,
    deviceName, // What happens when you change the device name?
    windowManager,
    appRendererUrl,
    appRendererOfflineSupport,
    logger,
    setIsEmpty,
  ]);

  // Set the window manager's renderer. If the renderer is null that means the boot step
  // just became 'play_playlist' and are waiting for the container element to be created.
  // The renderer can also be null if switching from 'play_playlist' to a boot step like
  // 'empty_playlist', where the container element is removed. We need to set the window
  // manager's renderer to null in case it was already playing to prevent creating new
  // windows when switching to 'empty_playlist'.
  useEffect(() => {
    if (windowManager) {
      windowManager.setRenderer(renderer);
    }
  }, [renderer, windowManager]);

  // Destroy the window manager when the component is unmounted.
  useEffect(() => {
    return () => {
      if (windowManager) {
        setWindowManager(null);
        windowManager.destroy();
        logger.info('Destroying window manager');
      }
    };
  }, [windowManager, logger]);

  return [windowManager, isEmpty];
}
